import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { APIURL } from '../../../../config';
import { Box, Grid, TextField, Select, MenuItem, InputLabel, Button, RadioGroup, FormControlLabel, Radio, IconButton, InputAdornment, FormControl, Autocomplete } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import DeleteIcon from '@mui/icons-material/Delete';


const PersonalDetails = ({
  formVals,
  customerList,
  states,
  edit,
  onRadioChangeHandler,
  formChangeHandler,
  deletePhoneNumber,
  addPhoneNumber,
  handlePhoneListChange,
  onAddAddress,
  onDeleteAddress,
  handleAddressListChange,
  addressList,
  onContinue,
  customerType,
  isStateNotNJ,
  getFieldLabel,
  isFieldRequired,
  isFieldVisible,
  errors = {}
}) => {
  
  const [selectedType, setSelectedType] = useState(formVals.cus_is_company ? 'Organization' : 'Individual');
  const isIndividual = selectedType !== 'Organization';
  const formatAddressDisplay = (a) => {
    const parts = [a.address_line_1, a.address_line_2, a.city, a.state, a.zip].filter(Boolean);
    return parts.join(', ');
  };
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressInputValues, setAddressInputValues] = useState(addressList.map(a => formatAddressDisplay(a)));

  // Debounce timeouts for each address field
  const addressDebounceTimeouts = useRef({});

  let visibilityStatus = ['first_name', 'last_name', 'company_name', 'DDDID', 'gender', 'dob', 'cus_local_id', 'email', 'address', 'phone', 'mailing_detail'].map((f) => ({f : isFieldVisible(f)}));

  useEffect(() => {
    setAddressInputValues(addressList.map(a => formatAddressDisplay(a)));
    setAddressSuggestions([]);
  }, [addressList]);

  const handleRadioChange = (e, val) => {
    setSelectedType(val);
    console.log('handleRadioChange', val);
    
    onRadioChangeHandler(val);
    formChangeHandler({ target: { id: 'cus_is_company', value: val !== 'Individual' } });
  };

  const handleAddressInputChange = (idx, input) => {
    // Clear previous timeout for this field
    if (addressDebounceTimeouts.current[idx]) {
      clearTimeout(addressDebounceTimeouts.current[idx]);
    }
    // Set new timeout for this field
    addressDebounceTimeouts.current[idx] = setTimeout(async () => {
      try {
        const res = await axios.get(APIURL + '/maps', { params: { input } });
        setAddressSuggestions(res.data.predictions || []);
      } catch (e) {
        setAddressSuggestions([]);
      }
    }, 300);

    setAddressInputValues(prev => {
      const newValues = [...prev];
      newValues[idx] = input;
      return newValues;
    });
  };
  

  const handleAddressSelect = async (idx, option) => {
    const res = await axios.get(APIURL + '/maps/details', { params: { place_id: option.place_id } });
    
    handleAddressListChange(idx, { target: { id: 'address_maps' }, value: res.data });

  };

  const duplicateDDD = formVals.DDDID && !!(customerList && customerList.find(entry => entry.ddd_id === formVals.DDDID));

   return (
     <Box sx={{ mt: 2 }}>
       {!edit && (
         <RadioGroup row name="customerType" value={selectedType} onChange={handleRadioChange}>
           {customerType.map((type) => (
             <FormControlLabel key={type} value={type} control={<Radio />} label={type} />
           ))}
         </RadioGroup>
       )}
  <Grid container spacing={2}>
         {isIndividual ? (
           <>
         {isFieldVisible('first_name') && (
         <Grid size={6}>
               <TextField
                 label={getFieldLabel('first_name','First Name')}
                 id="first_name"
                 name="first_name"
                 value={formVals.first_name || ''}
                 onChange={formChangeHandler}
                 required
                 fullWidth
                 error={!!errors.first_name}
                 inputProps={{ maxLength: 25 }}
               />
             </Grid>
             )}
             {isFieldVisible('last_name') && (
        <Grid size={6}>
               <TextField
                 label={getFieldLabel('last_name','Last Name')}
                 id="last_name"
                 name="last_name"
                 value={formVals.last_name || ''}
                 onChange={formChangeHandler}
                 required
                 fullWidth
                 error={!!errors.last_name}
                 inputProps={{ maxLength: 25 }}
               />
             </Grid>
             )}
           </>
         ) : (
           isFieldVisible('company_name') && (
        <Grid size={12}>
             <TextField
               label={getFieldLabel('company_name','Company Name')}
               id="company_name"
               name="company_name"
               value={formVals.company_name || ''}
               onChange={formChangeHandler}
               required
               fullWidth
               error={!!errors.company_name}
             />
           </Grid>
           )
         )}
         {isFieldVisible('DDDID') && (
      <Grid size={4}>
           <TextField
             label={getFieldLabel('DDDID','DDDID')}
             id="DDDID"
             name="DDDID"
             value={formVals.DDDID || ''}
             onChange={formChangeHandler}
             onInput={e => { e.target.value = e.target.value.replace(/\D/g, '').slice(0,10); }}
             inputProps={{ maxLength: 10 }}
             required={isFieldRequired('DDDID')}
             fullWidth
             error={!!errors.DDDID || duplicateDDD}
             helperText={duplicateDDD ? 'Already Exist.' : ''}
           />
         </Grid>
         )}
         {isFieldVisible('gender') && isIndividual && (
      <Grid size={4}>
           <FormControl fullWidth error={!!errors.gender}>
             <InputLabel id="gender-label">Gender *</InputLabel>
             <Select
               labelId="gender-label"
               id="gender"
               name="gender"
               value={formVals.gender || ''}
               label={getFieldLabel('gender','Gender')}
               required={isIndividual ? true : false}
               onChange={e => formChangeHandler({ target: { id: 'gender', value: e.target.value } })}
             >
               <MenuItem value="M">Male</MenuItem>
               <MenuItem value="F">Female</MenuItem>
               <MenuItem value="Other">Other</MenuItem>
             </Select>
           </FormControl>
         </Grid>
         )}
         {isFieldVisible('dob') && (
      <Grid size={isIndividual ? 4 : 8}>
           <LocalizationProvider dateAdapter={AdapterDayjs}>
             <DatePicker
               label={getFieldLabel('dob',isIndividual ? 'Date of Birth' : 'Incorporation Date')}
               value={formVals.dob ? dayjs(formVals.dob.replace(/T.*$/, '')) : null}
               onChange={d => {
                 if (d && d.isValid()) {
                   // Format as date only, no time or timezone
                   formChangeHandler({ target: { id: 'dob', value: d.format('YYYY-MM-DD') } });
                 }
               }}
               required={isIndividual ? true : false}
               openTo="year"
               views={['year', 'month', 'day']}
               slotProps={{ textField: { id: 'dob', required: isIndividual ? true : false, fullWidth: true, error: !!errors.dob } }}
             />
           </LocalizationProvider>
         </Grid>
         )}
         {isFieldVisible('cus_local_id') && (
      <Grid size={6}>
           <TextField
             label={getFieldLabel('cus_local_id','Local ID')}
             id="cus_local_id"
             name="cus_local_id"
             value={formVals.cus_local_id || ''}
             onChange={formChangeHandler}
             required={isFieldRequired('cus_local_id')}
             fullWidth
             error={!!errors.cus_local_id}
           />
         </Grid>
         )}
         {isFieldVisible('email') && (
      <Grid size={12}>
           <TextField
             label={getFieldLabel('email','E-mail')}
             id="email"
             name="email"
             value={formVals.email || ''}
             onChange={formChangeHandler}
             required
             fullWidth
             error={!!errors.email}
           />
         </Grid>
         )}
       </Grid>
       {isFieldVisible('address') && (addressList.length > 0 ? addressList : [{}]).map((add, idx) => (
          <Box key={idx} sx={{ mt: 2 }}>
            <Autocomplete
              freeSolo
              options={addressSuggestions}
              getOptionLabel={opt => opt.description || ''}
              inputValue={addressInputValues[idx] || ''}
              onInputChange={(e, v, r) => { handleAddressInputChange(idx, v) }}
              onChange={(e, v, r) => { if (r === 'selectOption') handleAddressSelect(idx, v); }}
              renderInput={params => (
                <TextField
                  {...params}
                  label={getFieldLabel('address', 'Address')}
                  required
                  fullWidth
                  error={!!errors.address && !add.address_line_1}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {addressList.length > 1 && (
                          <InputAdornment position="end">
                            <IconButton aria-label="delete address" onClick={() => onDeleteAddress(idx)} edge="end">
                              <DeleteIcon />
                            </IconButton>
                          </InputAdornment>
                        )}
                      </>
                    )
                  }}
                />
              )}
            />
          </Box>
        ))}
       {isFieldVisible('phone') && formVals.phnNumList.map(phn => (
         <Box key={phn.phnId} sx={{ mt: 2 }}>
           <TextField
             label={getFieldLabel('phone','Phone')}
             name="phone"
             required
             value={phn.phone}
             onChange={e => handlePhoneListChange(phn.phnId, e)}
             fullWidth
             error={!!errors.phone}
             inputProps={{ maxLength: 100 }}
             InputProps={{
               startAdornment: <InputAdornment position="start">{phn.country_code}</InputAdornment>,
               endAdornment: formVals.phnNumList.length > 1 && (
                 <InputAdornment position="end">
                   <IconButton aria-label="delete phone" onClick={() => deletePhoneNumber(phn.phnId)} edge="end">
                     <DeleteIcon />
                   </IconButton>
                 </InputAdornment>
               )
             }}
           />
         </Box>
       ))}
       {isFieldVisible('mailing_detail') && (
       <Box sx={{ mt: 2 }}>
         <TextField
           label={getFieldLabel('mailing_detail','Additional address details, if any')}
           id="mailing_detail"
           name="mailing_detail"
           value={formVals.mailing_detail || ''}
           onChange={formChangeHandler}
           fullWidth
           multiline
           maxLength={128}
         />
       </Box>
       )}
       <Box sx={{ mt: 2 }}>
         <Button variant="contained" fullWidth onClick={() => onContinue(null, 'other_details')}>
           Continue
         </Button>
       </Box>
     </Box>
   );
 }

 export default PersonalDetails;
